import React, { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import styles from './VerticalEntryForm.module.css'
import { useStepContext } from '@/components/Context/StepContext'
import { generateUrlSafeBase64 } from '@/components/utils/securityUtils'
import { handleFormSubmission } from '@/components/utils/formSubmissionUtils'
import { SERVICE_TYPES } from '@/components/constants'
import { WorkingStatusVertical } from '../Steps/StepContent/WorkingStatusVertical'
import { ExperienceYearsVertical } from '../Steps/StepContent/ExperienceYearsVertical'
import { PrefectureVertical } from '../Steps/StepContent/PrefectureVertical'
import { FullNameVertical } from '../Steps/StepContent/FullNameVertical'
import { TellVertical } from '../Steps/StepContent/TellVertical'
import StepCounter from '../Steps/StepCounter'
import { EntryFormProps } from './types'
import { JobChangeInterestModal } from '../FormGuidance/JobChangeInterestModal'
import { useAbTestPattern } from '@/components/utils/abTest'

const TOTAL_STEPS = 5

// 縦型EF（DEV-3061のA/Bテスト結果を受けDEV-3300で正式採用）。各stepの縦型専用コンポを
// currentStep 駆動で縦積み表示する。
const VerticalEntryForm = ({ prefectures, config }: EntryFormProps) => {
    const { currentStep, formData } = useStepContext()

    // DEV-3484: 初手ポップ(JobChangeInterestModal)はAのみ表示、Bは出さない。
    // ポップ非表示はhotelslaboのDEV-3379のB相当で、foodslaboのinstagram・googleAdは今も出している。
    // isResolvedを挟むのは、初回レンダーではcookie未読でisAが必ずtrueになり、B割当でも
    // モーダルが一度DOMに載るため（DEV-3379で実測）。モーダルはサフィックス無しの
    // data-view-label="EF_StepForm_Step0-転職意欲" を持つので、GTMが拾うとA側の計測値に混ざる。
    // EFのレイアウト（カード内バナー＋ステッパー先頭）はA/B共通なのでパターン判定を使わない
    const { isA, isResolved } = useAbTestPattern()

    const [registerToken, setRegisterToken] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isStuck, setIsStuck] = useState(false)
    // DEV-3061: 転職意欲確認ポップ(初手ポップ)。横型EntryFormと同じくstep0扱いで表示する
    const [step0ModalCompleted, setStep0ModalCompleted] = useState(false)

    useEffect(() => {
        setRegisterToken(generateUrlSafeBase64())
    }, [])

    const cards = [
        <WorkingStatusVertical gtmTags={config.stepFormLabels.step1} stepNumber={1} />,
        <ExperienceYearsVertical gtmTags={config.stepFormLabels.step2} stepNumber={2} />,
        <PrefectureVertical prefectures={prefectures} gtmTags={config.stepFormLabels.step3} stepNumber={3} />,
        <FullNameVertical gtmTags={config.stepFormLabels.step4} stepNumber={4} />,
        <TellVertical gtmTags={config.stepFormLabels.step5} stepNumber={5} isSubmitting={isSubmitting} />,
    ]

    // 新しいカードが出現（currentStepが前進）したらそのカードへ自動スクロール
    const cardRefs = useRef<Array<HTMLDivElement | null>>([])
    const prevStepRef = useRef(currentStep)
    useEffect(() => {
        if (currentStep > prevStepRef.current) {
            cardRefs.current[currentStep]?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            })
        }
        prevStepRef.current = currentStep
    }, [currentStep])

    // 通常配置のステッパーが画面上部から見えなくなったら固定版を表示する
    const sentinelRef = useRef<HTMLDivElement | null>(null)
    useEffect(() => {
        const el = sentinelRef.current
        if (!el || typeof IntersectionObserver === 'undefined') return
        const observer = new IntersectionObserver(
            ([entry]) => setIsStuck(!entry.isIntersecting),
            { threshold: 0 }
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        handleFormSubmission(
            e,
            formData,
            registerToken,
            setIsSubmitting,
            SERVICE_TYPES.FOODSLABO
        )
    }

    // DEV-3484: step1カードの内側に出すバナー。旧実装にあった上部の全幅オレンジバナー
    // （簡単30秒!／無料で求人を紹介してもらう！）はPO確認のうえ廃止した。A/B共通で、
    // instagram_asc・googleAdがDEV-3419で、tiktokがDEV-3480で入れたのと同じ形。
    // 1枚のカード内にしか描画されないため、旧実装で固定ヘッダー表示中にEF内のh1が2つに
    // なっていた問題（TODO(DEV-3300)）も解消する。ただしページ全体では
    // app/views/components/_office_box.html.erb:3 のh1（PC用マークアップ。SPでは不可視だが
    // DOMには存在）が残り、Aは現行FirstViewにもh1があるため、h1はBで2つ・Aで3つになる
    const cardBanner = (
        <header className={styles.cardBanner}>
            <h2 className={styles.cardBannerBadge}>
                簡単<span className={styles.cardBannerBadgeNumber}>30</span>
                <span className={styles.cardBannerBadgeUnit}>秒</span>！
            </h2>
            <h1 className={styles.cardBannerTitle}>無料転職サポート登録！</h1>
        </header>
    )

    return (
        <section id='entry-form' className={styles.vertical}>
            {isResolved && isA && config?.stepFormLabels.step0 && !step0ModalCompleted && (
                <JobChangeInterestModal
                    gtmTags={config.stepFormLabels.step0}
                    onComplete={() => {
                        setStep0ModalCompleted(true)
                        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
                    }}
                />
            )}

            {/* 上部画像の下に通常配置（白背景・シャドウなし） */}
            <div className={styles.inFlowHead} ref={sentinelRef}>
                <div className={styles.stepperPlain}>
                    <StepCounter currentIndex={currentStep} totalSteps={TOTAL_STEPS} />
                </div>
            </div>

            {/* 上記が見えなくなったら白背景＋シャドウ版を上部固定 */}
            {isStuck && (
                <div className={styles.fixedHead}>
                    <div className={styles.stepperFixed}>
                        <StepCounter currentIndex={currentStep} totalSteps={TOTAL_STEPS} />
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className={styles.form}>
                {cards.map((card, i) =>
                    i <= currentStep ? (
                        <div
                            key={i}
                            ref={(el) => {
                                cardRefs.current[i] = el
                            }}
                            className={styles.card}
                            style={
                                {
                                    '--ef-follow-display':
                                        i === currentStep ? 'block' : 'none',
                                } as CSSProperties
                            }
                        >
                            {i === 0 && cardBanner}
                            {card}
                        </div>
                    ) : null
                )}
            </form>

            {isSubmitting && <div className={styles.blockingOverlay} />}
        </section>
    )
}

export default VerticalEntryForm
